import {
  GuideTargetPivotedRowDto,
  GuideTargetTotalByTypeDto,
  GuideTargetsAvailablePeriodsDto,
} from '@act/shared/data-transfer-objects';
import {
  GuideTargetsUpdatedEventPayload,
  GuideTargetsUpdatedTypes,
} from '@act/shared/socket-events';
import {
  createSelector,
  createSlice,
  type PayloadAction,
} from '@reduxjs/toolkit';
import { useAppSelector } from 'redux/store-hooks';
import {
  defaultGuideTargetsFilters,
  type GuideTargetsFiltersType,
} from 'redux/types/guideTargetsTypes';
import { guideTargetsRequests } from 'redux/requests/guide-targets.request';
import { createApiThunk } from 'redux/utils/api-thunk';
import { addToEffectsMiddleware } from 'redux/utils/effects-middleware';
import { resetRedux } from '../actions/shared-actions';
import type { RootState } from '../reemaStore';
import type { WebsocketEventActions } from '../types/websocket-types';
import { PersistConfig, persistReducer } from 'redux-persist';
import localStorage from 'redux-persist/lib/storage';
import autoMergeLevel1 from 'redux-persist/es/stateReconciler/autoMergeLevel1';

export const getPaginatedGuideTargets = createApiThunk(
  guideTargetsRequests.getGuideTargets
);

export const getGuideTargetsAvailablePeriods = createApiThunk(
  guideTargetsRequests.getGuideTargetsAvailablePeriods
);

export interface GuideTargetsState {
  status: 'failed' | 'initial' | 'loading' | 'success';
  /** Only the latest list request may commit results — avoids out-of-order responses overwriting data. */
  latestPagedGuideTargetsRequestId: string | null;
  items: GuideTargetPivotedRowDto[];
  count: number;
  targetTypeKeys: string[];
  totalQueued: number;
  totalsByTargetType: GuideTargetTotalByTypeDto[];
  knownTargetTypeKeys: string[];
  knownServiceAreaKeys: string[];
  filters: GuideTargetsFiltersType;
  sideNavClientId: string | null;
  selectedYearMo: number | null;
  availablePeriodsStatus: 'failed' | 'initial' | 'loading' | 'success';
  availableYearMoValues: number[];
  targetsUpdated: number;
}

const initialState: GuideTargetsState = {
  status: 'initial',
  latestPagedGuideTargetsRequestId: null,
  items: [],
  count: 0,
  targetTypeKeys: [],
  totalQueued: 0,
  totalsByTargetType: [],
  knownTargetTypeKeys: [],
  knownServiceAreaKeys: [],
  filters: defaultGuideTargetsFilters(),
  sideNavClientId: null,
  selectedYearMo: null,
  availablePeriodsStatus: 'initial',
  availableYearMoValues: [],
  targetsUpdated: 0,
};

function mergeSortedUnique(existing: string[], incoming: string[]): string[] {
  const set = new Set([...existing, ...incoming]);
  return Array.from(set).sort((a, b) => a.localeCompare(b));
}

export const guideTargetsSlice = createSlice({
  name: 'guideTargets',
  initialState,
  reducers: {
    setGuideTargetsFilters: (
      state: GuideTargetsState,
      action: PayloadAction<Partial<GuideTargetsFiltersType>>
    ) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    resetGuideTargetsFilters: (state: GuideTargetsState) => {
      state.filters = defaultGuideTargetsFilters();
    },
    setGuideTargetsSideNavClientId: (
      state: GuideTargetsState,
      action: PayloadAction<string | null>
    ) => {
      state.sideNavClientId = action.payload;
    },
    setGuideTargetsSelectedYearMo: (
      state: GuideTargetsState,
      action: PayloadAction<number | null>
    ) => {
      state.selectedYearMo = action.payload;
    },
    clearGuideTargetsPagedResult: (state: GuideTargetsState) => {
      state.status = 'initial';
      state.latestPagedGuideTargetsRequestId = null;
      state.items = [];
      state.count = 0;
      state.targetTypeKeys = [];
      state.totalQueued = 0;
      state.totalsByTargetType = [];
      state.knownTargetTypeKeys = [];
      state.knownServiceAreaKeys = [];
    },
    guideTargetsUpdatedFromSocket: (
      state: GuideTargetsState,
      action: PayloadAction<GuideTargetsUpdatedEventPayload>
    ) => {
      const { yearMo } = action.payload ?? {};
      if (
        state.selectedYearMo != null &&
        (yearMo === undefined || yearMo === state.selectedYearMo)
      ) {
        state.targetsUpdated += 1;
      }
    },
  },
  extraReducers: builder => {
    builder
      .addCase(resetRedux, () => ({ ...initialState }))
      .addCase(getGuideTargetsAvailablePeriods.pending, state => {
        state.availablePeriodsStatus = 'loading';
      })
      .addCase(
        getGuideTargetsAvailablePeriods.fulfilled,
        (state, action: PayloadAction<GuideTargetsAvailablePeriodsDto>) => {
          state.availablePeriodsStatus = 'success';
          state.availableYearMoValues = action.payload.yearMoValues;
        }
      )
      .addCase(getGuideTargetsAvailablePeriods.rejected, state => {
        state.availablePeriodsStatus = 'failed';
        state.availableYearMoValues = [];
      })
      .addCase(getPaginatedGuideTargets.pending, (state, action) => {
        state.status = 'loading';
        state.latestPagedGuideTargetsRequestId = action.meta.requestId;
      })
      .addCase(getPaginatedGuideTargets.fulfilled, (state, action) => {
        if (action.meta.requestId !== state.latestPagedGuideTargetsRequestId) {
          return;
        }
        const payload = action.payload;
        const targetTypeKeys = payload.targetTypeKeys ?? [];
        const serviceAreaKeys = payload.serviceAreaKeys ?? [];
        state.status = 'success';
        state.items = payload.items ?? [];
        state.count = payload.count ?? 0;
        state.targetTypeKeys = targetTypeKeys;
        state.totalQueued = payload.totalQueued ?? 0;
        state.totalsByTargetType = payload.totalsByTargetType ?? [];
        state.knownTargetTypeKeys = mergeSortedUnique(
          state.knownTargetTypeKeys,
          targetTypeKeys
        );
        state.knownServiceAreaKeys = mergeSortedUnique(
          state.knownServiceAreaKeys,
          serviceAreaKeys
        );
      })
      .addCase(getPaginatedGuideTargets.rejected, (state, action) => {
        if (action.meta.requestId !== state.latestPagedGuideTargetsRequestId) {
          return;
        }
        state.status = 'failed';
        state.items = [];
      });
  },
});

export const {
  setGuideTargetsFilters,
  resetGuideTargetsFilters,
  setGuideTargetsSideNavClientId,
  setGuideTargetsSelectedYearMo,
  clearGuideTargetsPagedResult,
  guideTargetsUpdatedFromSocket,
} = guideTargetsSlice.actions;

const persistConfig: PersistConfig<GuideTargetsState> = {
  key: 'guide_targets',
  storage: localStorage,
  whitelist: ['filters', 'selectedYearMo', 'sideNavClientId'],
  stateReconciler: autoMergeLevel1,
  version: 3,
};

export const guideTargetsReducer = persistReducer(
  persistConfig,
  guideTargetsSlice.reducer
);

const selectGuideTargetsState = (state: RootState) => state.guideTargets;

const selectGuideTargetsFiltersState = createSelector(
  selectGuideTargetsState,
  s => s?.filters ?? defaultGuideTargetsFilters()
);

const selectGuideTargetsItems = createSelector(
  selectGuideTargetsState,
  s => s?.items ?? []
);

const selectGuideTargetsCount = createSelector(
  selectGuideTargetsState,
  s => s?.count ?? 0
);

const selectGuideTargetsStatus = createSelector(
  selectGuideTargetsState,
  s => s?.status ?? 'initial'
);

const selectGuideTargetsColumnKeys = createSelector(
  selectGuideTargetsState,
  s => s?.targetTypeKeys ?? []
);

const selectKnownTargetTypeKeys = createSelector(
  selectGuideTargetsState,
  s => s?.knownTargetTypeKeys ?? []
);

const selectKnownServiceAreaKeys = createSelector(
  selectGuideTargetsState,
  s => s?.knownServiceAreaKeys ?? []
);

const selectGuideTargetsSelectedYearMo = createSelector(
  selectGuideTargetsState,
  s => s?.selectedYearMo ?? null
);

const selectGuideTargetsSideNavClientId = createSelector(
  selectGuideTargetsState,
  s => s?.sideNavClientId ?? null
);

const selectGuideTargetsTotalQueued = createSelector(
  selectGuideTargetsState,
  s => s?.totalQueued ?? 0
);

const selectGuideTargetsTotalsByTargetType = createSelector(
  selectGuideTargetsState,
  s => s?.totalsByTargetType ?? []
);

const selectAvailablePeriodsStatus = createSelector(
  selectGuideTargetsState,
  s => s?.availablePeriodsStatus ?? 'initial'
);

const selectAvailableYearMoValues = createSelector(
  selectGuideTargetsState,
  s => s?.availableYearMoValues ?? []
);

const selectGuideTargetsTargetsUpdated = createSelector(
  selectGuideTargetsState,
  s => s?.targetsUpdated ?? 0
);

addToEffectsMiddleware(addEffect => {
  addEffect({
    action: guideTargetsUpdatedFromSocket,
    effect: async ({ dispatch }) => {
      void dispatch(getGuideTargetsAvailablePeriods({}));
    },
  });
});

export const guideTargetsSliceWebsocketActions: WebsocketEventActions[] = [
  {
    type: GuideTargetsUpdatedTypes.GUIDE_TARGETS_UPDATED,
    action: guideTargetsUpdatedFromSocket,
    channel: 'baseSocket',
  },
];

export const guideTargetsAppSelectors = {
  useSelectItems: () => useAppSelector(selectGuideTargetsItems),
  useSelectCount: () => useAppSelector(selectGuideTargetsCount),
  useSelectStatus: () => useAppSelector(selectGuideTargetsStatus),
  useSelectTargetTypeKeys: () => useAppSelector(selectGuideTargetsColumnKeys),
  useSelectKnownTargetTypeKeys: () => useAppSelector(selectKnownTargetTypeKeys),
  useSelectKnownServiceAreaKeys: () =>
    useAppSelector(selectKnownServiceAreaKeys),
  useSelectFilters: () => useAppSelector(selectGuideTargetsFiltersState),
  useSelectSelectedYearMo: () =>
    useAppSelector(selectGuideTargetsSelectedYearMo),
  useSelectSideNavClientId: () =>
    useAppSelector(selectGuideTargetsSideNavClientId),
  useSelectTotalQueued: () => useAppSelector(selectGuideTargetsTotalQueued),
  useSelectTotalsByTargetType: () =>
    useAppSelector(selectGuideTargetsTotalsByTargetType),
  useSelectAvailablePeriodsStatus: () =>
    useAppSelector(selectAvailablePeriodsStatus),
  useSelectAvailableYearMoValues: () =>
    useAppSelector(selectAvailableYearMoValues),
  useSelectTargetsUpdated: () =>
    useAppSelector(selectGuideTargetsTargetsUpdated),
};
